// src/pages/ForumsAboPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './ForumAbosPage.css';

import {
  subscribeToInboxMergedData,
  sendPlatformMessageData,
} from "../services/messagesDataService";

import {
  getMyForumPostsData,
  getMySubscribedForumPostsData,
  toggleForumSubscriptionData,
  subscribeToForumBadgeData,
} from "../services/forumDataService";

import GateIcon from '../components/GateIcon';
import OfflineBanner from '../components/OfflineBanner';
import LoginHintModal from '../components/LoginHintModal';
import SpeechIcon from "../components/SpeechIcon";
import ContentActionMenu from "../components/ContentActionMenu";
import { useBlockedUsers } from "../hooks/useBlockedUsers";

import { useAuth } from "../auth/AuthProvider";


const SEEN_REPLIES_KEY = "forum_seen_replies_v1";

const readSeenRepliesMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeSeenRepliesMap = (map) => {
  try {
    localStorage.setItem(SEEN_REPLIES_KEY, JSON.stringify(map));
  } catch {}
};


const ForumAbosPage = () => {
  const navigate = useNavigate();

  const { user } = useAuth();
  const myUid = user?.uid || null;
  const isVerified = user?.emailVerified === true;
const {
  filterBlocked,
  addBlockedUser,
  isBlocked,
  blockedUserIds,
} = useBlockedUsers(myUid, isVerified);

  const [unreadCount, setUnreadCount] = useState(0);
  const [seenRepliesMap, setSeenRepliesMap] = useState(() => readSeenRepliesMap());

  // zusätzliche Badge-Daten für "Meine Beiträge" im Dropdown
  const [myPostsForBadge, setMyPostsForBadge] = useState([]);

  const [myReplyActivityMap, setMyReplyActivityMap] = useState({});
  const [abosReplyActivityMap, setAbosReplyActivityMap] = useState({});
  

  useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initUnread = () => {
    if (!myUid) {
      setUnreadCount(0);
      return;
    }

    try {
      unsub = subscribeToInboxMergedData(myUid, (merged) => {
  if (!mounted) return;

  const list = Array.isArray(merged) ? merged : [];
  const c = list.reduce((acc, m) => {
  const senderId = m?.senderId || "";
  const isUnread = m?.recipientId === myUid && m?.read === false;

  if (!isUnread) return acc;

  // ✅ Nachrichten von blockierten Nutzern nicht zählen
  if (senderId && isBlocked(senderId)) return acc;

  return acc + 1;
}, 0);

  setUnreadCount(c);
});
    } catch {
      if (mounted) setUnreadCount(0);
    }
  };

  initUnread();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid, isBlocked, blockedUserIds]);

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [abonniert, setAbonniert] = useState([]);

  const [postToUnsubscribe, setPostToUnsubscribe] = useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalRecipient, setModalRecipient] = useState({
    uid: '',
    name: '',
  });
  const [newMessageText, setNewMessageText] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [abosError, setAbosError] = useState('');

  const [showLoginHint, setShowLoginHint] = useState(false);
  const [actionToast, setActionToast] = useState({ visible: false, text: "" });
  const showActionToast = (text) => {
  setActionToast({ visible: true, text });

  setTimeout(() => {
    setActionToast({ visible: false, text: "" });
  }, 2200);
};

  const postsPerPage = 20;

  const toMillis = (ts) => {
    if (!ts) return 0;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts === 'string') return Date.parse(ts) || 0;
    return 0;
  };
const toDisplayMillis = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return 0;
};

const formatCreatedDate = (item) => {
  const millis =
    toDisplayMillis(item?.createdAt) ||
    toDisplayMillis(item?.createdAtClient) ||
    toDisplayMillis(item?.createdAtMs) ||
    toDisplayMillis(item?.updatedAt) ||
    toDisplayMillis(item?.updatedAtClient);

  if (!millis) return "";

  return new Date(millis).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch('https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js', {
          method: 'HEAD',
          cache: 'no-store',
        });
        setIsOffline(false);
      } catch (err) {
        console.warn('Keine Server-Verbindung möglich (ForumAbosPage):', err);
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  useEffect(() => {
  if (!myUid) {
    setShowLoginHint(true);
  }
}, [myUid]);

 useEffect(() => {
  let mounted = true;

  const loadAbos = async () => {
    if (!myUid) {
      setAbonniert([]);
      setMyPostsForBadge([]);
      setAbosError("");
      return;
    }

    try {
      const [myPosts, subscribedPosts] = await Promise.all([
        getMyForumPostsData(myUid),
        getMySubscribedForumPostsData(myUid),
      ]);

      if (!mounted) return;

      const sortPosts = (list) =>
        Array.isArray(list)
          ? [...list].sort((a, b) => {
              const at = toMillis(a.createdAt) || toMillis(a.createdAtClient);
              const bt = toMillis(b.createdAt) || toMillis(b.createdAtClient);
              return bt - at;
            })
          : [];

      const sortedMyPosts = sortPosts(myPosts);
      const sortedAbos = sortPosts(subscribedPosts);

      setMyPostsForBadge(sortedMyPosts);
      setAbonniert(sortedAbos);

      const newTotalPages = Math.max(1, Math.ceil(sortedAbos.length / postsPerPage) || 1);
      setCurrentPage((p) => Math.min(p, newTotalPages));

      setAbosError("");
    } catch (err) {
      console.warn("⚠️ Fehler beim Laden der Abos:", err);
      if (!mounted) return;
      setAbonniert([]);
      setMyPostsForBadge([]);
      setAbosError("Deine abonnierten Beiträge konnten aktuell nicht geladen werden.");
    }
  };

  loadAbos();

  return () => {
    mounted = false;
  };
}, [myUid, retryAttempt]);

useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initBadges = async () => {
    if (!myUid) {
      setMyReplyActivityMap({});
      setAbosReplyActivityMap({});
      return;
    }

    try {
      unsub = await subscribeToForumBadgeData(myUid, ({ myReplies, abosReplies }) => {
        if (!mounted) return;

        setMyReplyActivityMap(myReplies || {});
        setAbosReplyActivityMap(abosReplies || {});
      });
    } catch (err) {
      console.warn("ForumAbosPage: subscribeToForumBadgeData fehlgeschlagen:", err);
      if (mounted) {
        setMyReplyActivityMap({});
        setAbosReplyActivityMap({});
      }
    }
  };

  initBadges();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid]);


  const myUnreadReplyCount = Object.values(myReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  // ✅ Antworten von blockierten Nutzern nicht zählen
  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

const abosUnreadReplyCount = Object.values(abosReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  // ✅ Antworten von blockierten Nutzern nicht zählen
  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

  const dropdownKey = `${myUid || "guest"}-${myUnreadReplyCount}-${abosUnreadReplyCount}-${unreadCount}`;

 const markPostAsSeen = (postId) => {
  const meta =
    abosReplyActivityMap[postId] ||
    myReplyActivityMap[postId] ||
    null;

  const currentSeen = Number(seenRepliesMap[postId] || 0);
  const ts = Math.max(currentSeen, Number(meta?.latestTs || 0), Date.now());

  setSeenRepliesMap((prev) => {
    const next = { ...prev, [postId]: ts };
    writeSeenRepliesMap(next);
    return next;
  });
};

  const handleOpenPost = (postId) => {
    markPostAsSeen(postId);
    navigate(`/forum/beitrag/${postId}`);
  };

  const visibleAbonniert = useMemo(() => {
  return filterBlocked(abonniert);
}, [abonniert, filterBlocked]);

const totalPages = Math.max(1, Math.ceil(visibleAbonniert.length / postsPerPage) || 1);

const pagedPosts = visibleAbonniert.slice(
  (currentPage - 1) * postsPerPage,
  currentPage * postsPerPage
);

  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (value === 'abos') return;
    navigate(value ? `/forum/${value}` : '/forum');
  };

  const authorLabel = (post) => post.authorName || post.author || 'Unbekannt';

  const handleUserClick = (post, e) => {
    e.stopPropagation();

    if (!myUid) {
      setShowLoginHint(true);
      return;
    }

    const recipientUid = post.userId || post.authorId || '';
    const recipientName = post.authorName || post.author || 'Unbekannt';

    if (!recipientUid) {
      alert('Empfänger konnte nicht ermittelt werden (userId/authorId fehlt im Post).');
      return;
    }

    setModalRecipient({ uid: recipientUid, name: recipientName });
    setShowMessageModal(true);
  };

  const confirmUnsubscribe = async () => {
    const id = postToUnsubscribe;
    if (!id || !myUid) return;

    try {
      await toggleForumSubscriptionData(id, myUid, false);
    } catch (err) {
      console.warn('⚠️ Firestore-Update beim Abbestellen fehlgeschlagen:', err);
      alert('Konnte das Abo nicht entfernen. Bitte erneut versuchen.');
    }

    setPostToUnsubscribe(null);
  };

  const handleSendMessage = async () => {
    const text = newMessageText.trim();
    if (!text) return;

    if (!myUid) {
      setShowMessageModal(false);
      setShowLoginHint(true);
      return;
    }

    try {
      await sendPlatformMessageData({
  senderId: myUid,
  recipientId: modalRecipient.uid,
  senderEmail: user?.email || "",
  recipientEmail: "",
  senderName: user?.displayName || "",
  recipientName: modalRecipient.name || "Unbekannt",
  text,
  contextType: "forumSubscription",
  contextId: null,
});
    } catch (err) {
      console.warn('⚠️ Nachricht konnte nicht gesendet werden:', err);
      alert('Die Nachricht konnte nicht gesendet werden. Bitte später erneut versuchen.');
    }

    setNewMessageText('');
    setShowMessageModal(false);
  };

  return (
    <div className="forum-container">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      {showLoginHint && <LoginHintModal onClose={() => setShowLoginHint(false)} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate('/')}
      />
      <SpeechIcon
  className="speech-icon no-tap-highlight"
  unreadCount={unreadCount}
  myUnreadReplyCount={myUnreadReplyCount}
  abosUnreadReplyCount={abosUnreadReplyCount}
/>

<GateIcon className="login-icon" />

<img
  src="/rezept-icon.png"
  alt="Rezepthelfer Icon"
  className="rezept-icon"
  onClick={() => navigate('/rezepte')}
/>

      <p className="top-claim">Zusammen wachsen - im Forum!</p>
      <div className="page-background" />

      <div className="forum-main-box">
        {actionToast.visible && (
  <div className="mainbox-toast">
    {actionToast.text}
  </div>
)}
        <div className="forum-dropdown">
          <div className="custom-select-wrapper" key={`forum-abos-dropdown-${dropdownKey}`}>
          <select
          value="abos"
           onChange={handleDropdownChange}
           key={`forum-abos-select-${dropdownKey}`}
            >
              <option value="">Alle Beiträge</option>
              <option value="my">{myUnreadReplyCount > 0 ? "🔴" : ""} Meine Beiträge</option>
              <option value="abos">{abosUnreadReplyCount > 0 ? "🔴" : ""} Abonnierte Beiträge</option>
              <option value="messages">{unreadCount > 0 ? "🔴" : ""} Nachrichten</option>
            </select>
            <span className="arrow-down">▼</span>
          </div>
        </div>

        {abosError && <div className="error-message forum-error">{abosError}</div>}

        {!myUid ? (
          <p>Bitte logge dich ein, um deine abonnierten Beiträge zu sehen.</p>
        ) : pagedPosts.length === 0 ? (
          <p>Du hast noch keine Beiträge abonniert.</p>
        ) : (
         pagedPosts.map((post) => (
  <div className="message-row" key={post.id}>
    {abosReplyActivityMap[post.id]?.latestTs >
  Number(seenRepliesMap[post.id] || 0) &&
  abosReplyActivityMap[post.id]?.latestAuthorId &&
  abosReplyActivityMap[post.id]?.latestAuthorId !== myUid &&
  !isBlocked(abosReplyActivityMap[post.id]?.latestAuthorId) && (
    <span className="unread-icon">❗</span>
)}
    <div
      className="post-box compact-post hoverable"
      onClick={() => handleOpenPost(post.id)}
    >
      <div
  className="compact-post-actions"
  onClick={(e) => e.stopPropagation()}
>
  <span
    className="bell-icon clickable"
    onClick={(e) => {
      e.stopPropagation();
      setPostToUnsubscribe(post.id);
    }}
    title="Abo entfernen"
  >
    🔔
  </span>

  <ContentActionMenu
  contentType="forumPost"
  contentId={post.id}
  ownerId={post.userId || post.authorId || post.ownerId || null}
  ownerName={authorLabel(post)}

  contentTitle={post.title || "Beitrag bei Edens Ernte"}
  contentText={post.content || ""}

  currentUser={user}
  shareLabel="Beitrag teilen"
  reportLabel="Beitrag melden"
  blockLabel="Nutzer blockieren"
  shareTitle={post.title || "Beitrag bei Edens Ernte"}
  shareText={post.content || ""}
  shareUrl={`/forum/beitrag/${post.id}`}
  onLoginRequired={() => setShowLoginHint(true)}
  onActionToast={showActionToast}
  onBlockedUser={(blockedUserId) => {
    addBlockedUser(blockedUserId);
  }}
/>
</div>

      {formatCreatedDate(post) && (
  <div className="forum-card-date">
    Erstellt am {formatCreatedDate(post)}
  </div>
)}

<h3>{post.title}</h3>
<p>{post.content}</p>

<span
  className="author-bottom-right clickable"
  onClick={(e) => handleUserClick(post, e)}
  title="Nachricht schreiben"
>
  {authorLabel(post)}
</span>
    </div>
  </div>
))
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ⏮
            </button>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 10))}>«</button>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 10))}>»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              ⏭
            </button>
          </div>
        )}
      </div>

      {postToUnsubscribe !== null && (
        <div className="modal-overlay" onClick={() => setPostToUnsubscribe(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Beitrag wirklich nicht mehr abonnieren?</h3>
            <div className="modal-buttons">
              <button className="delete-btn" onClick={confirmUnsubscribe}>
                Ja
              </button>
              <button className="cancel-btn" onClick={() => setPostToUnsubscribe(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showMessageModal && (
        <div className="footer-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="footer-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Nachricht an {modalRecipient.name}</h3>
            <textarea
              placeholder="Nachricht schreiben..."
              rows="5"
              style={{ width: '100%', marginBottom: '12px' }}
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
            />
            <button className="green-button" onClick={handleSendMessage}>
              Nachricht senden
            </button>
            <button
              className="red-button"
              onClick={() => setShowMessageModal(false)}
              style={{ marginTop: '10px' }}
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumAbosPage;